import type {
  EntitiesSessionResponse,
  EntitiesUserResponse
} from '@axilon-apps/domains-identity/state'
import type { IngestIntake } from '@axilon-apps/domains-ontology-ui-state'

const EXPIRING_WINDOW_MS = 30 * 24 * 60 * 60 * 1000

export type ApiKeyExpiryState = 'active' | 'expiring' | 'expired' | 'unknown' | 'none'

export interface ApiKeyExpiry {
  at: string | null
  state: ApiKeyExpiryState
}

function parsedTime(value: string | null | undefined): number | null {
  if (!value) return null
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : null
}

// An API key is a service-account user; its tokens are the sessions the
// listing embeds. Absent means the listing did not ask for them.
export function apiKeySessions(apiKey: EntitiesUserResponse): EntitiesSessionResponse[] {
  return apiKey.sessions ?? []
}

export function apiKeyExpiry(sessions: EntitiesSessionResponse[], now = Date.now()): ApiKeyExpiry {
  if (sessions.length === 0) return { at: null, state: 'none' }

  const dated = sessions
    .map((session) => ({ at: session.expiresAt ?? null, time: parsedTime(session.expiresAt) }))
    .filter((entry): entry is { at: string; time: number } => entry.time !== null)
  const future = dated.filter(({ time }) => time > now).sort((a, b) => a.time - b.time)
  if (future[0]) {
    return {
      at: future[0].at,
      state: future[0].time - now <= EXPIRING_WINDOW_MS ? 'expiring' : 'active'
    }
  }

  if (dated.length !== sessions.length) return { at: null, state: 'unknown' }

  const past = dated.sort((a, b) => b.time - a.time)
  if (past[0]) return { at: past[0].at, state: 'expired' }
  return { at: null, state: 'unknown' }
}

export function newestLastUsed(sessions: EntitiesSessionResponse[]): string | null {
  return (
    sessions
      .map((session) => ({
        value: session.lastUsedAt ?? null,
        time: parsedTime(session.lastUsedAt)
      }))
      .filter(
        (entry): entry is { value: string; time: number } =>
          entry.value !== null && entry.time !== null
      )
      .sort((a, b) => b.time - a.time)[0]?.value ?? null
  )
}

export function enabledIntakes(intakes: IngestIntake[]): IngestIntake[] {
  return intakes.filter((intake) => intake.enabled)
}

export function sortedApiKeys(keys: EntitiesUserResponse[]): EntitiesUserResponse[] {
  return [...keys].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
}
